import type { Editor, NodeViewProps, Range } from "@tiptap/core";
import { escapeForRegEx, mergeAttributes, Node } from "@tiptap/core";
import { type Accessor, type Component, createUniqueId, onCleanup, untrack } from "solid-js";
import type { IconName } from "../../icon/Icon";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { isEmpty, isEqual, isNil } from "lodash-es";
import type { ResolvedPos } from "@tiptap/pm/model";
import { NodeViewWrapper } from "../../wysiwyg/tiptap/solid/NodeViewWrapper";
import { SolidNodeViewRenderer } from "../../wysiwyg/tiptap/solid/NodeViewRenderer";
import type { Delimiter } from "../../../utils/stringTemplateUtil";
import FieldId from "../../../fields/FieldId";
import type { ValueReference } from "../../../types/ValueReference";
import FieldValueReference from "../../../fields/FieldValueReference";
import VariableValueReference from "../../../variables/VariableValueReference";
import type VariableId from "../../../variables/VariableId";
import ClosablePill from "../../menu/ClosablePill";
import SingleSelectWithSearch from "../../menu/SingleSelectWithSearch";
import { useTranslation } from "../../../state/i18n";
import type { DynamicValueId, DynamicValueOptionType, SelectOptions } from "../../../types/Menu";
import { inferDynamicValueOptionType } from "../../../utils/placeholderUtil.ts";

export type Placeholder = {
  value: string | ValueReference;
  icon: IconName;
  label: string;
  orphaned: boolean;
};

const getDelimiterStart = (delimiter: Accessor<Delimiter>): string => delimiter() as string;

export type MenuPosition = "inline" | "block";

type InputAttrs = {
  options: () => SelectOptions<DynamicValueId>;
  menuPosition: MenuPosition;
  select: (
    value: string | ValueReference | VariableId | FieldId,
    valueType: DynamicValueOptionType,
    iconName: IconName,
    label: string,
    deleteNode: () => void,
  ) => void;
  delimiter: Accessor<Delimiter>;
};

export const initPlaceholder = (
  getEditor: () => Accessor<Editor | null>,
  delimiter: Accessor<Delimiter>,
  options: () => SelectOptions<DynamicValueId>,
  menuPosition: MenuPosition,
) => {
  const addPill = (
    value: string | ValueReference | VariableId | FieldId,
    valueType: DynamicValueOptionType,
    iconName: IconName,
    label: string,
  ): void => {
    const editor = getEditor();
    if (!isNil(editor())) {
      let type;
      if (value instanceof FieldId || value instanceof FieldValueReference) {
        type = "field";
      } else if (value instanceof VariableValueReference) {
        type = "variable";
      } else {
        type = "metaVariable";
      }

      editor()
        ?.chain()
        .focus()
        .insertContent({
          type: "pillComponent",
          attrs: {
            type,
            valuetype: valueType,
            value,
            iconName,
            label,
          },
        })
        .run();
    }
  };

  const inputAttrs: InputAttrs = {
    options,
    menuPosition,
    select: (value, valueType, iconName, label, deleteNode) => {
      addPill(value, valueType, iconName, label);
      deleteNode();
    },
    delimiter,
  };

  const addPlaceholder = () => {
    const editor = getEditor();
    editor()?.commands.insertContent({
      type: "inputComponent",
      attrs: inputAttrs,
    });
  };

  return {
    extensions: [ParentNode(inputAttrs), PillNode(untrack(() => delimiter)), InputNode],
    addPlaceholder,
    options,
  };
};

const pluginKey = new PluginKey("detectDelimiter");

const ParentNode = (activeAttrs: InputAttrs) =>
  Node.create({
    name: "parentNode",
    topNode: true,
    content: "(text|pillComponent|inputComponent)*",
    addProseMirrorPlugins() {
      return [
        new Plugin<Range | undefined>({
          key: pluginKey,
          editor: this.editor,

          state: {
            init: (): Range | undefined => undefined,
            apply: (tr, value) => {
              if (!isNil(value)) {
                return undefined;
              }

              const delimiter = getDelimiterStart(activeAttrs.delimiter);
              const match = findMatch(delimiter, tr.selection.$from);
              if (!isNil(match)) {
                return match;
              } else {
                return undefined;
              }
            },
          },

          appendTransaction: (_, oldState, newState) => {
            const oldPluginState = pluginKey.getState(oldState);
            const newPluginState = pluginKey.getState(newState);

            if (isNil(oldPluginState) && !isNil(newPluginState)) {
              const node = newState.schema.nodes.inputComponent.create(activeAttrs);
              return newState.tr.replaceRangeWith(newPluginState.from, newPluginState.to, node);
            }

            return null;
          },
        }),
      ];
    },
  });

const findMatch = (char: string, $position: ResolvedPos): Range | undefined => {
  const escapedChar = escapeForRegEx(char);
  const regexString = `(?:^)?${escapedChar}[^\\s${escapedChar}]*`;
  const regExp = new RegExp(regexString, "gm");

  const text = ($position.nodeBefore?.isText ?? false) ? $position.nodeBefore?.text : false;

  if (isNil(text) || text === false) {
    return undefined;
  }

  const textFrom = $position.pos - text.length;
  const match = Array.from(text.matchAll(regExp)).pop();

  if (isEmpty(match) || isNil(match?.input) || isNil(match?.index)) {
    return undefined;
  }

  const from = textFrom + match.index;
  const to = from + match[0].length;

  return {
    from,
    to,
  };
};

const PillNode = (delimiter: Accessor<Delimiter>) =>
  Node.create({
    name: "pillComponent",
    group: "inline",
    inline: true,
    atom: true,
    selectable: false,

    addAttributes: () => ({
      type: {
        isRequired: true,
      },
      valuetype: {
        isRequired: true,
      },
      value: {
        isRequired: true,
      },
      iconName: {
        isRequired: true,
      },
      label: {
        isRequired: true,
      },
      orphaned: {
        default: false,
      },
    }),

    parseHTML: () => [
      {
        tag: "pill-component",
      },
    ],

    renderHTML: ({ HTMLAttributes }) => ["pill-component", mergeAttributes(HTMLAttributes)],

    renderText: ({ node }) => {
      const delimiterString = getDelimiterStart(delimiter);
      return `${delimiterString}${node.attrs.value}}`;
    },

    addNodeView: () => SolidNodeViewRenderer(PillComponent),
  });

const PillComponent: Component<NodeViewProps> = (props) => (
  <NodeViewWrapper as="span" class="px-px font-sans">
    <ClosablePill
      data-node-view-anchor
      label={props.node.attrs.label}
      icon={props.node.attrs.iconName}
      variant={props.node.attrs.orphaned === true ? "destructive" : "neutral"}
      onClick={() => {
        const from = props.getPos();
        if (!isNil(from)) {
          const to = from + props.node.nodeSize;
          props.editor.chain().focus().deleteRange({ from, to }).blur().run();
        }
      }}
      classList={{ "z-10": true }}
    />
  </NodeViewWrapper>
);

const InputNode = Node.create({
  name: "inputComponent",
  group: "inline",
  inline: true,
  atom: true,
  selectable: false,
  addAttributes: () => ({
    options: {
      isRequired: true,
    },
    select: {
      isRequired: true,
    },
    menuPosition: {
      isRequired: true,
    },
    delimiter: {
      isRequired: true,
    },
  }),
  // Explicitly render empty HTML value so it never appears in output.
  renderHTML: () => ["span", {}],
  addNodeView: () => SolidNodeViewRenderer(InputComponent),
});

const InputComponent = (props: NodeViewProps) => {
  const { t } = useTranslation();

  const menu = (
    <SingleSelectWithSearch<DynamicValueId>
      id={createUniqueId()}
      ariaLabel={t(($) => $.MIXED_INPUT_MENU_ARIA_LABEL)}
      onSelect={(value, flattenedOptions) => {
        const option = flattenedOptions.find((option) => isEqual(option.value, value));
        if (!isNil(option)) {
          if (option.variant !== "dynamicValue" || isNil(option.icon)) {
            return;
          }
          const select = props.node.attrs.select as InputAttrs["select"];
          const valueType = inferDynamicValueOptionType(option.value) ?? "text";
          select(option.value, valueType, option.icon.name, option.label, props.deleteNode);
        }
      }}
      options={(props.node.attrs.options as unknown as () => SelectOptions<DynamicValueId>)()}
      width={props.node.attrs.menuPosition === "block" ? "matchTrigger" : "480px"}
      hideTrigger
      onClose={props.deleteNode}
    />
  ) as HTMLDivElement;

  setTimeout(() => {
    const selectTrigger = document.querySelector(
      "[data-id='hidden-dynamic-value-single-select-trigger']",
    ) as HTMLButtonElement;
    if (!isNil(selectTrigger)) {
      selectTrigger.click();
    }
  }, 0);

  // Closes the placeholder menu when clicking the placeholder button again
  const placeholderButtons = Array.from(
    document.querySelectorAll('[data-placeholder-button="true"]'),
  ) as HTMLButtonElement[];
  const placeholderButtonEvent = (e: MouseEvent) => {
    e.stopPropagation();
    props.deleteNode();
  };

  placeholderButtons.forEach((placeholderButton) =>
    placeholderButton.addEventListener("click", placeholderButtonEvent),
  );

  onCleanup(() => {
    placeholderButtons.forEach((placeholderButton) =>
      placeholderButton.removeEventListener("click", placeholderButtonEvent),
    );
  });

  return (
    <NodeViewWrapper
      as="span"
      class="text-base-regular"
      classList={{ relative: props.node.attrs.menuPosition === "inline" }}
    >
      {getDelimiterStart(props.node.attrs.delimiter as unknown as Accessor<Delimiter>)}|{"} "}
      {menu}
    </NodeViewWrapper>
  );
};
